import { API_ROUTES_FOR_AXIOS } from '../constants/ApiRoutes';
import { axiosInstance } from '../constants/axiosInstance';
import { ApiResponse, PaginatedResponse } from 'src/types/commonResponseTypes';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import {
  CheckDnsVerificationApiResponse,
  CreateOrganizationPayload,
  InviteUsersViaEmailResponse,
  KnowledgeValidationMode,
  OnboardingApiResponse,
  OrganizationApiResponse,
  OrganizationApiResponseWithPopulatedPlan,
  OrganizationMembershipWithPopulatedOrg,
  BulkAddUsersToOrganizationByEmailResponse,
  BulkUpdateUsersActiveOrganizationByEmailResponse,
  OrganizationDomainPeerSuggestion,
  OrganizationDomainSuggestion,
  OrganizationJoinRequest,
  OrganizationJoinRequestDecisionPayload,
  ResolveOrganizationInvitationCodeResponse,
} from 'src/types/organization';
import { OnboardingDetailsPayload } from 'src/types/registrationFormTypes';
import { failureResponse } from 'src/utils/responseUtils';
import { handleError } from 'src/utils/commonErrorUtils';

export const checkDnsVerification = async (): Promise<
  ApiResponse<CheckDnsVerificationApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['checkDnsVerification']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateOrganizationDetails = async (
  body: Omit<Partial<OrganizationApiResponse>, 'planId' | '_id'>,
): Promise<ApiResponse<OrganizationApiResponseWithPopulatedPlan>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateOrganization']();
    const { data } = await axiosInstance.patch(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createOrganizationDetails = async (
  payload: CreateOrganizationPayload,
): Promise<ApiResponse<OrganizationApiResponseWithPopulatedPlan>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['createOrganization']();
    const { data } = await axiosInstance.post(apiUrl, payload);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const saveOnboardingDetails = async (
  payload: OnboardingDetailsPayload,
): Promise<ApiResponse<OnboardingApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['saveOnboardingDetails']();
    const { data } = await axiosInstance.post(apiUrl, payload);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getSupportedTimezones = async (): Promise<
  ApiResponse<string[]>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getSupportedTimezones']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getCurrentOrganizationDetails = async (): Promise<
  ApiResponse<OrganizationApiResponseWithPopulatedPlan>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getActiveOrganizationDetails']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getAllOrganizationsUserIsMemberOf = async (
  page: number,
  limit: number,
): Promise<
  ApiResponse<PaginatedResponse<OrganizationMembershipWithPopulatedOrg[]>>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getAllOrganizationsUserIsMemberOf'](
      page,
      limit,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const validateOrganizationId = async (
  orgId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['validateOrganizationId'](orgId);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteOrganizationById = async (
  orgId: string,
): Promise<ApiResponse<OrganizationApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['deleteOrganizationById'](orgId);
    const { data } = await axiosInstance.delete(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deactivateAccount = async (): Promise<
  ApiResponse<OrganizationApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['deactivateAccount']();
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const confirmOrganizationDeletion = async (
  organizationName: string,
): Promise<ApiResponse<string>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['confirmOrganizationDeletion']();
    const { data } = await axiosInstance.post(apiUrl, { organizationName });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const reactivateAccount = async (): Promise<ApiResponse<string>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['reactivateAccount']();
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const checkIfAccountDeactivated = async (): Promise<
  ApiResponse<OrganizationApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['checkIfAccountDeactivated']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateResponseControlCardState = async (
  allowResponseControlCard: boolean,
): Promise<ApiResponse<OrganizationApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateResponseControlCardState']();
    const { data } = await axiosInstance.patch(apiUrl, {
      allowResponseControlCard,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateResponseMetadataState = async (
  allowResponseMetadata: boolean,
): Promise<ApiResponse<OrganizationApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateResponseMetadataState']();
    const { data } = await axiosInstance.patch(apiUrl, {
      allowResponseMetadata,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updatePrivateChatState = async (
  allowPrivateChat: boolean,
): Promise<ApiResponse<OrganizationApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updatePrivateChatState']();
    const { data } = await axiosInstance.patch(apiUrl, {
      allowPrivateChat,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateKnowledgeValidationMode = async (
  knowledgeValidationMode: KnowledgeValidationMode,
): Promise<ApiResponse<OrganizationApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateOrganization']();
    const { data } = await axiosInstance.patch(apiUrl, {
      knowledgeValidationMode,
    });

    if (
      data?.content &&
      typeof data.content === 'object' &&
      'planId' in data.content &&
      data.content.planId &&
      typeof data.content.planId === 'object' &&
      '_id' in data.content.planId
    ) {
      return {
        ...data,
        content: {
          ...data.content,
          planId: String(data.content.planId._id),
        },
      };
    }

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const joinOrganizationWithInviteToken = async (
  inviteToken: string,
): Promise<ApiResponse<OrganizationMembershipWithPopulatedOrg>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['joinOrganizationWithInviteToken'](inviteToken);
    const { data } = await axiosInstance.get(apiUrl);

    if (data && data.message && typeof data.message !== 'string') {
      data.message = JSON.stringify(data.message);
    }

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const resolveOrganizationInvitationCode = async (
  code: string,
): Promise<ApiResponse<ResolveOrganizationInvitationCodeResponse>> => {
  try {
    const { data } = await axiosInstance.post(
      API_ROUTES_FOR_AXIOS['resolveOrganizationInvitationCode'](),
      { code },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getInvitedUsersByOrgId = async <T>(
  page: number,
  limit: number,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<T>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getInvitedUsersByOrgId'](
      page,
      limit,
      search,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const inviteUsersToOrgViaEmail = async (
  emails: string[],
): Promise<ApiResponse<InviteUsersViaEmailResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['inviteUsersToOrgViaEmail']();
    const { data } = await axiosInstance.post(apiUrl, {
      emails,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const bulkAddUsersToOrganizationByEmail = async (
  orgId: string,
  file: File,
): Promise<ApiResponse<BulkAddUsersToOrganizationByEmailResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['bulkAddUsersToOrgByEmail'](orgId);
    const formData = new FormData();

    formData.append('file', file);
    const { data } = await axiosInstance.post<
      ApiResponse<BulkAddUsersToOrganizationByEmailResponse>
    >(apiUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const bulkUpdateUsersActiveOrganizationByEmail = async (
  orgId: string,
  file: File,
): Promise<ApiResponse<BulkUpdateUsersActiveOrganizationByEmailResponse>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['bulkUpdateUsersActiveOrgByEmail'](orgId);
    const formData = new FormData();

    formData.append('file', file);
    const { data } = await axiosInstance.post<
      ApiResponse<BulkUpdateUsersActiveOrganizationByEmailResponse>
    >(apiUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const changeActiveOrganization = async (
  organizationId: string,
): Promise<ApiResponse<OrganizationMembershipWithPopulatedOrg>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['changeActiveOrganization']();
    const { data } = await axiosInstance.post(apiUrl, {
      newActiveOrganizationId: organizationId,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getOrganizationsForDomainDirectory = async (): Promise<
  ApiResponse<OrganizationDomainSuggestion[]>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getOrganizationsForDomainDirectory']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};

export const lookupOrganizationsForDomainDirectoryByEmail = async (): Promise<
  ApiResponse<OrganizationDomainSuggestion[]>
> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['lookupOrganizationsForDomainDirectoryByEmail']();
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};

export const getUsersForCorporateDomainDirectory = async (): Promise<
  ApiResponse<OrganizationDomainPeerSuggestion[]>
> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['getUsersForCorporateDomainDirectory']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};

export const createOrganizationJoinRequest = async (
  organizationId: string,
  requestMessage?: string,
): Promise<ApiResponse<OrganizationJoinRequest>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['createOrganizationJoinRequest']();
    const payload: {
      organizationId: string;
      requesterMessage?: string;
    } = {
      organizationId,
    };

    if (requestMessage) {
      payload.requesterMessage = requestMessage;
    }

    const { data } = await axiosInstance.post(apiUrl, payload);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};

export const listOrganizationJoinRequests = async (
  status?: string,
  page?: number,
  limit?: number,
): Promise<ApiResponse<PaginatedResponse<OrganizationJoinRequest[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['listOrganizationJoinRequests'](
      status,
      page,
      limit,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};

export const handleOrganizationJoinRequestDecision = async (
  requestId: string,
  payload: OrganizationJoinRequestDecisionPayload,
): Promise<ApiResponse<OrganizationJoinRequest>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS['handleOrganizationJoinRequestDecision'](requestId);
    const { data } = await axiosInstance.patch(apiUrl, payload);

    return data;
  } catch (error: any) {
    return failureResponse(
      error?.response?.data?.message ?? UNHANDLED_ERROR,
      error?.response?.data?.statusCode,
    );
  }
};
